import { defineStore } from "pinia";
import {
  CoinExpenditure,
  CoinIncome,
  CoinQuestTaskType,
  CoinQuestUser,
  CoinQuestRepeatType,
} from "@/types/coin";
import {
  createOneCoinQuestUser,
  createOneCoinVote,
  readAllCoinQuestUser,
  readCoinExpenditurePage,
  readCoinIncomePage,
} from "@/apis/coin";
import {
  cursorEncoderById,
  cursorPagerStoreFactory,
} from "@/stores/factory/list-store-factory";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { computed, ref } from "vue";
import { useGlobalUser } from "@/stores/globalUser";

export const useCoin = defineStore("coin", () => {
  /** 硬币获取记录状态维护 */
  const questList = ref<CoinQuestUser[]>([]);
  const refreshQuestList = async () => {
    const { itemzz } = await readAllCoinQuestUser();
    questList.value = itemzz;
  };
  const sortedQuestList = computed(() => {
    return questList.value.sort((a, b) => b.id - a.id);
  });
  const questType2QuesCountMap = computed(() => {
    const map = new Map<CoinQuestTaskType, number>();
    for (const item of questList.value) {
      if (
        item.coinQuest.repeatType === CoinQuestRepeatType.Always &&
        item.obtainAmount === item.coinQuest.requireAmount
      ) {
        // 常驻任务，且已经完成，不计入统计
        continue;
      }
      if (map.has(item.coinQuest.id)) {
        const oldValue = map.get(item.coinQuest.id);
        if (item.obtainAmount > (oldValue ?? 0)) {
          map.set(item.coinQuest.id, item.obtainAmount);
        }
      } else {
        map.set(item.coinQuest.id, item.obtainAmount);
      }
    }
    return map;
  });

  /** 硬币花费记录状态维护 */
  const expenditureMap = entityStoreFactory<CoinExpenditure>("id");
  const { updateMap: updateQuestMap } = expenditureMap;
  const {
    dataList: expenditureList,
    initListIfAbsent: initExpenditureListIfAbsent,
    fetchNewerList: fetchNewerExpenditureList,
    fetchOlderList: fetchOlderExpenditureList,
  } = cursorPagerStoreFactory<CoinExpenditure>({
    entityStore: expenditureMap,
    fetch: async (cursor) => {
      const response = await readCoinExpenditurePage({
        cursor,
        sort: { field: "id", order: "desc" },
      });
      updateQuestMap(response.page.data);
      return response.page.data;
    },
    cursorEncoder: cursorEncoderById,
  });
  /** 硬币收入记录状态维护 */
  const incomeMap = entityStoreFactory<CoinIncome>("id");
  const { updateMap: updateIncomeMap } = incomeMap;
  const {
    dataList: incomeList,
    initListIfAbsent: initIncomeListIfAbsent,
    fetchNewerList: fetchNewerIncomeList,
    fetchOlderList: fetchOlderIncomeList,
  } = cursorPagerStoreFactory<CoinIncome>({
    entityStore: incomeMap,
    fetch: async (cursor) => {
      const response = await readCoinIncomePage({
        cursor,
        sort: { field: "id", order: "desc" },
      });
      updateIncomeMap(response.page.data);
      return response.page.data;
    },
    cursorEncoder: cursorEncoderById,
  });

  /** 签到 */
  const hasCheckedToday = computed(() => {
    return questList.value.some(
      (item) => item.coinQuest.id === CoinQuestTaskType.Visit
    );
  });
  const dailyCheck = async () => {
    const user = useGlobalUser();
    await createOneCoinQuestUser(CoinQuestTaskType.Visit);
    await user.refreshUser();
  };

  /** 投票 */
  const vote = async (params: Parameters<typeof createOneCoinVote>[0]) => {
    await createOneCoinVote(params);
  };

  /** 今日剩余可投票数 */
  const restExchangeableTicketToday = computed(() => {
    const user = useGlobalUser();
    const amount = Math.max(1, Math.ceil(user.creator_score / 10));
    return Math.max(amount - user.coinVoteToday, 0);
  });

  return {
    /** 状态 */
    restExchangeableTicketToday,
    sortedQuestList,
    expenditureList,
    incomeList,
    hasCheckedToday,
    questType2QuesCountMap,
    /** 方法 */
    refreshQuestList,
    initExpenditureListIfAbsent,
    fetchNewerExpenditureList,
    fetchOlderExpenditureList,
    initIncomeListIfAbsent,
    fetchNewerIncomeList,
    fetchOlderIncomeList,
    dailyCheck,
    vote,
  };
});

export const NORMAL_TICKETS_PER_GOLD_TICKET = 100;
